import React, { useCallback, useState } from 'react';
import {
    Alert,
    AlertIcon,
    AlertTitle,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Grid,
    GridItem,
    Input,
    Select,
    Textarea,
    useToast
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useClientApiFetch } from 'ts/client/common/hooks';
import DataConsentCheckbox from 'ts/common/components/DataConsentCheckbox';
import {
    getFullPaymentAmount,
    getPartialPaymentAmount
} from 'ts/common/components/invoice/helpers';
import Braintree3DSChallenge from 'ts/common/components/payments/Braintree3DSChallenge';
import LegacyCreditCardCVVInput from 'ts/common/components/payments/LegacyCreditCardCVVInput';
import LegacyCreditCardNumberInput from 'ts/common/components/payments/LegacyCreditCardNumberInput';
import {
    getExpirationMonths,
    getExpirationYears
} from 'ts/common/components/payments/creditCardUtils';
import { FetchContentType, FetchMethod, IFetchHookResponse, useTranslate } from 'ts/common/hooks';
import { useBootstrapData } from 'client_react/bootstrap';
import ClientPaymentForm from 'client_react/common/components/ClientPaymentForm';
import { type ClientPaymentOption } from 'client_react/common/components/ClientPaymentOptions';
import InvoicePaymentOptions from 'client_react/invoice/components/InvoicePaymentOptions';
import InvoicePaymentSummary from 'client_react/invoice/components/InvoicePaymentSummary';

interface FormFields {
    nameOnCard: string;
    cardNumber: string;
    expirationMonth: string;
    expirationYear: number;
    cvvCode: string;
    billingPostalCode: string;
    emailAddress: string;
    note?: string;
    doesAcceptTerms?: boolean;
}

interface PaymentResponse {
    info?: {
        transactionErrors?: {
            unexpected?: string;
        };
    };
    type: string;
}

interface Props {
    customAmount: number;
    paymentAmountType: ClientPaymentOption;
    setCustomAmount: (amount: number) => void;
    setPaymentAmountType: (x: ClientPaymentOption) => void;
}

const LegacyInvoicePaymentForm: React.FC<Props> = ({
    customAmount,
    paymentAmountType,
    setCustomAmount,
    setPaymentAmountType
}) => {
    const navigate = useNavigate();
    const toast = useToast();
    const t = useTranslate('clientInvoice');
    const { braintreeToken, has3DSEnabled, isSubjectToGdpr } = useBootstrapData();

    const { invoice, refetchInvoice } =
        useOutletContext<{ invoice: SpApi.Client.IInvoice; refetchInvoice: () => Promise<void> }>();

    const [cardType, setCardType] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [is3DSModalOpen, setIs3DSModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);

    const form = useForm<FormFields>({ mode: 'onTouched' });

    const { performFetch: createInvoicePayment } = useClientApiFetch<PaymentResponse>(
        `brand/${invoice.brand.id}/invoice/${invoice.publicId}/payment`,
        {
            defer: true,
            headers: {
                'Content-Type': FetchContentType.SpJson
            },
            method: FetchMethod.POST
        }
    );

    const handleClickBack = useCallback(() => {
        navigate(`/invoice/${invoice.publicId}`);
    }, [invoice, navigate]);

    const chargeAmount =
        paymentAmountType === 'full'
            ? getFullPaymentAmount(invoice)
            : paymentAmountType === 'retainer'
            ? getPartialPaymentAmount(invoice)
            : customAmount;

    const maxPaymentAmount = getFullPaymentAmount(invoice);
    const isCustomAmountValid =
        paymentAmountType !== 'custom' || (customAmount >= 1 && customAmount <= maxPaymentAmount);

    const submitPayment = useCallback(
        async (creditCard: SpApi.IInvoiceCreditCard) => {
            const { note } = form.getValues();

            const now = new Date();
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

            const paymentData = {
                amount: chargeAmount,
                creditCard,
                invoiceId: invoice.publicId,
                ...(note && { note }),
                paymentDate: startOfDay,
                paymentFundsType: 'credit-card',
                type: 'invoice-payment'
            };

            let response: IFetchHookResponse<PaymentResponse> | undefined = undefined;

            response = await createInvoicePayment({
                body: JSON.stringify(paymentData)
            });

            if (!response || response.status >= 400) {
                const errorMessage =
                    response?.info?.transactionErrors?.unexpected || t('error.unexpectedError');

                setErrorMessage(errorMessage);
                setIsSubmitting(false);
                setIs3DSModalOpen(false);

                return;
            }

            await refetchInvoice();

            navigate(`/invoice/${invoice.publicId}`);

            toast({
                title: t('paymentSuccess'),
                status: 'success'
            });
        },
        [chargeAmount, form, invoice, createInvoicePayment, refetchInvoice, navigate, toast, t]
    );

    const handle3DSClose = useCallback(() => {
        setIsSubmitting(false);
        setIs3DSModalOpen(false);
    }, []);

    const handleSubmit = useCallback(
        async (formData: FormFields) => {
            setErrorMessage(null);
            setIsSubmitting(true);

            // If using Braintree with 3DS, perform verification first:
            if (has3DSEnabled) {
                setIs3DSModalOpen(true);
                // The 3DS modal will handle the actual payment submission
                return;
            }

            // Otherwise, do direct payment submission for non-3DS gateways:
            const creditCard: SpApi.IInvoiceCreditCard = {
                nameOnCard: formData.nameOnCard,
                cardNumber: formData.cardNumber?.replace(/\s/g, ''),
                expirationMonth: formData.expirationMonth,
                expirationYear: formData.expirationYear,
                cvvCode: formData.cvvCode,
                billingPostalCode: formData.billingPostalCode,
                emailAddress: formData.emailAddress,
                enableAutoPay: false
            };

            await submitPayment(creditCard);
        },
        [has3DSEnabled, submitPayment, setIs3DSModalOpen]
    );

    return (
        <FormProvider {...form}>
            <ClientPaymentForm
                backButtonText={t('back')}
                billingInformationFields={
                    <>
                        <FormControl
                            isRequired
                            isInvalid={!!form.formState.errors.nameOnCard}
                            paddingBottom={0}
                        >
                            <FormLabel>{t('nameOnCard')}</FormLabel>
                            <Input {...form.register('nameOnCard', { required: true })} />
                            <FormErrorMessage>
                                {form.formState.errors.nameOnCard?.message}
                            </FormErrorMessage>
                        </FormControl>

                        <FormControl
                            isRequired
                            isInvalid={!!form.formState.errors.cardNumber}
                            paddingBottom={0}
                        >
                            <FormLabel>{t('cardNumber')}</FormLabel>
                            <LegacyCreditCardNumberInput
                                name="cardNumber"
                                onCardTypeChange={setCardType}
                            />
                            <FormErrorMessage>
                                {form.formState.errors.cardNumber?.message}
                            </FormErrorMessage>
                        </FormControl>

                        <Grid templateColumns="1fr 1fr" gap={4}>
                            <GridItem>
                                <FormControl
                                    isRequired
                                    isInvalid={!!form.formState.errors.expirationMonth}
                                    paddingBottom={0}
                                >
                                    <FormLabel>{t('expirationMonth')}</FormLabel>
                                    <Select
                                        {...form.register('expirationMonth', {
                                            required: true
                                        })}
                                    >
                                        <option value="">{t('selectMonth')}</option>
                                        {getExpirationMonths().map((month) => (
                                            <option key={month.value as string} value={month.value}>
                                                {month.label}
                                            </option>
                                        ))}
                                    </Select>
                                    <FormErrorMessage>
                                        {form.formState.errors.expirationMonth?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem>
                                <FormControl
                                    isRequired
                                    isInvalid={!!form.formState.errors.expirationYear}
                                    paddingBottom={0}
                                >
                                    <FormLabel>{t('expirationYear')}</FormLabel>
                                    <Select
                                        {...form.register('expirationYear', { required: true })}
                                    >
                                        <option value="">{t('selectYear')}</option>
                                        {getExpirationYears().map((year) => (
                                            <option key={year.value as number} value={year.value}>
                                                {year.label}
                                            </option>
                                        ))}
                                    </Select>
                                    <FormErrorMessage>
                                        {form.formState.errors.expirationYear?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>
                        </Grid>

                        <Grid templateColumns="1fr 1fr" gap={4}>
                            <GridItem>
                                <FormControl
                                    isRequired
                                    isInvalid={!!form.formState.errors.cvvCode}
                                    paddingBottom={0}
                                >
                                    <FormLabel>{t('cvvCode')}</FormLabel>
                                    <LegacyCreditCardCVVInput name="cvvCode" cardType={cardType} />
                                    <FormErrorMessage>
                                        {form.formState.errors.cvvCode?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            <GridItem>
                                <FormControl
                                    isRequired
                                    isInvalid={!!form.formState.errors.billingPostalCode}
                                    paddingBottom={0}
                                >
                                    <FormLabel>{t('billingPostalCode')}</FormLabel>
                                    <Input
                                        {...form.register('billingPostalCode', {
                                            required: true
                                        })}
                                    />
                                    <FormErrorMessage>
                                        {form.formState.errors.billingPostalCode?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>
                        </Grid>

                        <FormControl
                            isRequired
                            isInvalid={!!form.formState.errors.emailAddress}
                            paddingBottom={0}
                        >
                            <FormLabel>{t('email')}</FormLabel>
                            <Input
                                type="email"
                                {...form.register('emailAddress', {
                                    required: true,
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                        message: t('invalidEmail')
                                    }
                                })}
                            />
                            <FormErrorMessage>
                                {form.formState.errors.emailAddress?.message}
                            </FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!form.formState.errors.note} paddingBottom={0}>
                            <FormLabel>{t('note')}</FormLabel>
                            <Textarea
                                {...form.register('note', {
                                    maxLength: {
                                        value: 1000,
                                        message: t('noteTooLong')
                                    }
                                })}
                                maxLength={1000}
                            />
                            <FormErrorMessage>
                                {form.formState.errors.note?.message}
                            </FormErrorMessage>
                        </FormControl>

                        {isSubjectToGdpr && (
                            <DataConsentCheckbox register={form.register} translate={t} />
                        )}

                        {errorMessage && (
                            <Alert status="error">
                                <AlertIcon />
                                <AlertTitle>{errorMessage}</AlertTitle>
                            </Alert>
                        )}
                    </>
                }
                billingInformationTitle={t('billingInformation')}
                isSubmitDisabled={isSubmitting || !form.formState.isValid || !isCustomAmountValid}
                isSubmitLoading={isSubmitting}
                onClickBack={handleClickBack}
                onSubmit={form.handleSubmit(handleSubmit)}
                paymentOptions={
                    <InvoicePaymentOptions
                        customAmount={customAmount}
                        paymentAmountType={paymentAmountType}
                        setCustomAmount={setCustomAmount}
                        setPaymentAmountType={setPaymentAmountType}
                    />
                }
                paymentSummary={
                    <InvoicePaymentSummary
                        customAmount={customAmount}
                        paymentAmountType={paymentAmountType}
                    />
                }
                submitButtonText={t('payInvoice')}
            />

            {has3DSEnabled && (
                <Braintree3DSChallenge
                    isOpen={is3DSModalOpen}
                    creditCardDetails={form.watch()}
                    chargeAmount={chargeAmount}
                    braintreeToken={braintreeToken}
                    onSubmitPayment={submitPayment}
                    onClose={handle3DSClose}
                />
            )}
        </FormProvider>
    );
};

export default LegacyInvoicePaymentForm;
